import { TRANSITION_CLASSES } from '../../constants/cssClasses.js';
import { VIEW_TYPES } from '../../constants/viewTypes.js';

/**
 * TransitionManager - Handles view transitions and fade animations
 * Manages CSS classes for fade in/out effects and height transitions
 * 
 * @class TransitionManager
 */
class TransitionManager {
    // Maximum time to wait for animation to complete before forcing recovery
    static ANIMATION_TIMEOUT = 2000;

    constructor() {
        this._originalMainContentStyles = null;
        this._transitionTimeoutId = null;
        this._animationTimeoutId = null;
    }

    /**
     * Start main content transition (fade out)
     * @param {HTMLElement} mainContent - The main content DOM element
     * @param {string} currentViewType - Current view type (productDetail, productGuideBook, etc)
     */
    startTransition(mainContent, currentViewType) {
        if (!mainContent) {
            console.warn('[TransitionManager] startTransition called with null/undefined mainContent');
            return;
        }
        
        // Clear any pending transition cleanup timeout
        if (this._transitionTimeoutId) {
            clearTimeout(this._transitionTimeoutId);
            this._transitionTimeoutId = null;
        }
        
        // Clear any pending animation timeout
        if (this._animationTimeoutId) {
            clearTimeout(this._animationTimeoutId);
            this._animationTimeoutId = null;
        }
        
        // Clear previous styles before starting a new transition
        this._originalMainContentStyles = null;
        
        // Validate and sanitize currentViewType
        if (typeof currentViewType !== 'string') {
            console.warn('[TransitionManager] startTransition called with invalid viewType:', currentViewType);
            currentViewType = VIEW_TYPES.DEFAULT;
        }
        
        try {
            // Force cleanup of any previous transition state
            mainContent.classList.remove(TRANSITION_CLASSES.FADE_IN, TRANSITION_CLASSES.FADE_OUT, TRANSITION_CLASSES.PRODUCT_DETAIL_FADE_FIX);
            
            // Force a reflow to ensure CSS reset takes effect
            try {
                void mainContent.offsetHeight;
            } catch (reflowError) {
                console.warn('[TransitionManager] Error forcing reflow:', reflowError);
            }
            
            if (currentViewType === VIEW_TYPES.PRODUCT_DETAIL) {
                mainContent.classList.add(TRANSITION_CLASSES.PRODUCT_DETAIL_FADE_FIX);
                const computed = window.getComputedStyle(mainContent);
                this._originalMainContentStyles = {
                    height: mainContent.style.height,
                    paddingTop: mainContent.style.paddingTop,
                    paddingBottom: mainContent.style.paddingBottom,
                    overflow: mainContent.style.overflow,
                    boxSizing: mainContent.style.boxSizing
                };
                const height = mainContent.offsetHeight + 'px';
                mainContent.style.height = height;
                mainContent.style.paddingTop = computed.paddingTop;
                mainContent.style.paddingBottom = computed.paddingBottom;
                mainContent.style.overflow = 'hidden';
                mainContent.style.boxSizing = 'border-box';
                
                // Set auto-cleanup timeout if endTransition doesn't complete
                // Prevents memory leak if navigation interrupts the transition
                this._transitionTimeoutId = setTimeout(() => {
                    if (this._originalMainContentStyles) {
                        console.warn('[TransitionManager] Transition cleanup timeout - releasing cached styles');
                        this._originalMainContentStyles = null;
                    }
                    this._transitionTimeoutId = null;
                }, 10000);
            }
            mainContent.classList.add(TRANSITION_CLASSES.FADE_OUT);
        } catch (error) {
            console.error('[TransitionManager] Error in startTransition:', error);
        }
    }

    /**
     * Schedule a callback using Knockout's task scheduler with fallback to setTimeout
     * Provides robust error handling for Ko.tasks API dependency
     * 
     * @private
     * @param {Function} callback - Function to schedule
     * @returns {boolean} True if scheduled via ko.tasks, false if fell back to setTimeout
     */
    #scheduleRestore(callback) {
        try {
            // Use optional chaining for safer property access
            if (window.ko?.tasks?.schedule && typeof window.ko.tasks.schedule === 'function') {
                window.ko.tasks.schedule(callback);
                return true;
            }
        } catch (error) {
            console.warn('[TransitionManager] Error calling ko.tasks.schedule:', error);
        }
        
        // Fallback to immediate setTimeout if ko.tasks unavailable or errored
        setTimeout(callback, 0);
        return false;
    }

    /**
     * End main content transition (fade in)
     * @param {HTMLElement} mainContent - The main content DOM element
     * @param {string} currentViewType - Current view type
     */
    endTransition(mainContent, currentViewType) {
        if (!mainContent) {
            console.warn('[TransitionManager] endTransition called with null/undefined mainContent');
            return;
        }
        
        // Clear the auto-cleanup timeout since we're handling cleanup normally
        if (this._transitionTimeoutId) {
            clearTimeout(this._transitionTimeoutId);
            this._transitionTimeoutId = null;
        }
        
        // Clear any pending animation timeout
        if (this._animationTimeoutId) {
            clearTimeout(this._animationTimeoutId);
            this._animationTimeoutId = null;
        }
        
        // Validate currentViewType
        if (typeof currentViewType !== 'string') {
            console.warn('[TransitionManager] endTransition called with invalid viewType:', currentViewType);
            currentViewType = VIEW_TYPES.DEFAULT;
        }
        
        const restore = () => {
            try {
                mainContent.classList.remove(TRANSITION_CLASSES.FADE_OUT);
                mainContent.classList.add(TRANSITION_CLASSES.FADE_IN);
                // Don't restore styles for guidebook views - let CSS classes control the layout
                if (this._originalMainContentStyles && currentViewType !== VIEW_TYPES.PRODUCT_GUIDE_BOOK) {
                    mainContent.style.height = this._originalMainContentStyles.height;
                    mainContent.style.paddingTop = this._originalMainContentStyles.paddingTop;
                    mainContent.style.paddingBottom = this._originalMainContentStyles.paddingBottom;
                    mainContent.style.overflow = this._originalMainContentStyles.overflow;
                    mainContent.style.boxSizing = this._originalMainContentStyles.boxSizing;
                    this._originalMainContentStyles = null;
                }
            } catch (error) {
                console.error('[TransitionManager] Error in endTransition restore callback:', error);
            }
        };
        
        // Schedule restore with robust fallback
        const usedKoTasks = this.#scheduleRestore(restore);
        
        // Set safety timeout to force animation to complete if it hangs
        // Checks if FADE_OUT is still present, indicating animation didn't finish
        this._animationTimeoutId = setTimeout(() => {
            if (mainContent.classList.contains(TRANSITION_CLASSES.FADE_OUT)) {
                console.warn('[TransitionManager] Animation timeout - forcing restore after', TransitionManager.ANIMATION_TIMEOUT, 'ms (scheduled via', (usedKoTasks ? 'ko.tasks' : 'setTimeout'), ')');
                restore();
            }
            this._animationTimeoutId = null;
        }, TransitionManager.ANIMATION_TIMEOUT);
    }

    /**
     * Promise-based delay for smooth transitions
     * @param {number} ms - Delay in milliseconds
     * @returns {Promise<void>} Resolves after delay
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Clear saved transition styles
     */
    clearStyles() {
        this._originalMainContentStyles = null;
    }
}

export default TransitionManager;
